// 存取權統一清單（SPEC #vms-web-787 IAM-10、#org-tree-ui 第 2 點「誰能存取」；VMS UX 評審 2026-10-13 P0-9）。
// 一列一筆「誰在哪裡能做什麼」：對象｜範圍（完整路徑）｜能做什麼｜來源｜期限｜〔撤銷〕。資料 GET /v1/orgs/{org}/access（lib/accessApi.ts）。
// 同一個元件用在：人員與存取 › 存取權（上方篩選「人」「範圍」「來源」）、成員抽屜（principal = 那個人，IAM-07）、組織與場域 › 誰能存取（node = 那個節點）。
// 撤銷照來源打原本的 API（伺服器再驗一次權限；can_revoke 只是決定按鈕畫不畫）：
//   組織角色的列不撤銷：〔變更角色…〕開成員抽屜，移出組織只在抽屜的維護區（SPEC IAM-10，第二輪 B2-06）；家戶角色 = DELETE /v1/homes/{home}/members/{user}；場域授權 = DELETE …/nodes/{node}/grants/{grant}；
//   節點指派 = DELETE …/nodes/{node}/assignments/{id}（節點管理員先跑影響預覽）；分享 = DELETE /v1/homes/{home}/shares/{share}；委派 = 到委派詳情結束。
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { kinioFetch, planeBase } from '@kinio/web-sdk';
import { useConfirm } from '../../components/ConfirmDialog';
import { accessApi, ACCESS_SOURCES, principalName, rowTerm, rowWhat, type AccessRow, type AccessSource } from '../../lib/accessApi';
import { orgApi, shareApi, type MyOrg, type OrgRef } from '../../lib/planeApi';
import { siteRows } from '../../lib/siteTree';
import { orgPath, rootSite, siteNamePath, PATH_SEP } from '../../lib/orgRoot';
import { treeApi } from '../../lib/orgTreeApi';
import { actionWords } from '../../lib/orgTreeUi';
import { PreviewDialog } from '../orgtree/PreviewDialog';
import { useMemberGroups, useOrgMembers } from '../orgtree/hooks';
import { otErr } from '../orgtree/otErr';
import { GrantWizard, type GrantPreset } from './GrantWizard';

/** 分享那一列的家戶：後端有帶就用；沒帶就到這個組織的家戶逐一找那筆分享（只在按〔撤銷〕時做，最多 50 戶）。 */
export async function findShareHome(o: OrgRef, row: Pick<AccessRow, 'home_id' | 'scope' | 'source_ref'>): Promise<string | null> {
  if (row.home_id) return row.home_id;
  if (row.scope.home_id) return row.scope.home_id;
  const homes = (await orgApi.homes(o)).slice(0, 50);
  for (const h of homes) {
    const list = await shareApi.list(o.api, h.home_id).catch(() => []);
    if (list.some((s) => s.share_id === row.source_ref)) return h.home_id;
  }
  return null;
}

export function AccessList({
  org,
  principal,
  node,
  filters,
  grantPreset,
  compact,
}: {
  org: MyOrg;
  /** 固定篩這個人 / 群組（成員抽屜） */
  principal?: string;
  /** 固定篩這個節點（組織與場域 › 誰能存取） */
  node?: { node_id: string; name: string };
  /** 顯示上方篩選列（存取權分頁）；篩選放網址 ?principal=&node=&source= */
  filters?: boolean;
  /** 〔＋ 給存取權〕開精靈時預先選好的 */
  grantPreset?: GrantPreset;
  compact?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const { ask, dialog } = useConfirm();
  const [sp, setSp] = useSearchParams();
  const f = {
    principal: principal ?? (filters ? (sp.get('principal') ?? '') : ''),
    node: node?.node_id ?? (filters ? (sp.get('node') ?? '') : ''),
    source: (filters ? (sp.get('source') ?? '') : '') as AccessSource | '',
  };
  const setF = (k: 'principal' | 'node' | 'source', v: string) => {
    const n = new URLSearchParams(sp);
    if (v) n.set(k, v);
    else n.delete(k);
    setSp(n, { replace: true });
  };
  const key = ['org-access', org.org_id, f.principal, f.node, f.source];
  const q = useInfiniteQuery({
    queryKey: key,
    queryFn: ({ pageParam }) => accessApi.list(org, { principal: f.principal, node: f.node, source: f.source, cursor: pageParam || undefined, limit: 100 }),
    initialPageParam: '',
    getNextPageParam: (last) => last.next_cursor || undefined,
    retry: false,
  });
  const rows = useMemo(() => (q.data?.pages ?? []).flatMap((p) => p.data), [q.data]);
  const members = useOrgMembers(org, !!filters);
  const groups = useMemberGroups(org, !!filters && org.kind === 'team');
  // 根節點的顯示（第二輪 B2-04）：路徑從組織名起算、根節點寫「<組織名>（整個組織）」；誰能存取的範圍欄寫相對路徑（B2-05）
  const sites = useQuery({ queryKey: ['org-sites', org.org_id], queryFn: () => orgApi.sites(org), staleTime: 60_000 });
  const root = useMemo(() => rootSite(sites.data ?? []), [sites.data]);
  const wholeOrg = t('ot.whole_org_name', { org: org.display_name });
  // 根節點可能不只一個（舊資料）；都換成組織名（第三輪 B3-05）
  const roots = useMemo(() => (sites.data ?? []).filter((x) => !x.parent_site_id), [sites.data]);
  const fullPath = (p: string) => orgPath(p, roots.length ? roots : sites.isError ? undefined : root, org.display_name, wholeOrg);
  const nodeFull = useMemo(() => {
    if (!node) return null;
    if (root && node.node_id === root.site_id) return org.display_name;
    return siteNamePath(sites.data ?? [], node.node_id, org.display_name)?.join(PATH_SEP) ?? null;
  }, [node, root, sites.data, org.display_name]);
  const where = (r: AccessRow) => {
    if (!node) return fullPath(r.scope.path);
    if (r.scope.kind === 'node' && r.scope.id === node.node_id) return root?.site_id === node.node_id ? wholeOrg : (nodeFull ?? fullPath(r.scope.path));
    if (root && r.scope.kind === 'node' && r.scope.id === root.site_id) return wholeOrg;
    // 全站同一種路徑寫法：一律從組織名起算（第四輪 B4-11：這裡原本寫相對路徑「lsdg › rd」，人員與存取寫「LivingStyle › lsdg › rd」）
    const full = fullPath(r.scope.path);
    return full;
  };
  const nodeOpts = useMemo(
    () =>
      siteRows(sites.data ?? [])
        .filter((r) => r.site.status !== 'archived')
        .map((r) => ({
          ...r,
          // siteRows 的路徑用「 / 」、第一段是根節點名 → 換成組織名；根節點本身 =「<組織名>（整個組織）」
          path: !r.site.parent_site_id ? wholeOrg : root && r.path.startsWith(root.name + ' / ') ? org.display_name + r.path.slice(root.name.length) : r.path,
        })),
    [sites.data, root, wholeOrg, org.display_name],
  );
  const [granting, setGranting] = useState(false);
  const [previewRevoke, setPreviewRevoke] = useState<AccessRow | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['org-access', org.org_id] });
    void qc.invalidateQueries({ queryKey: ['ot', org.org_id] });
    void qc.invalidateQueries({ queryKey: ['org-members', org.org_id] });
  };
  const words = (a: string[]) => actionWords(t, a) || '—';

  // 存取清單的列沒有版本號：撤銷前讀節點上那筆指派的目前版本（平台要 expected_version）
  async function currentAssignment(r: AccessRow) {
    const a = (await treeApi.access(org, r.scope.id)).assignments.find((x) => x.assignment_id === r.source_ref);
    if (!a) throw new Error(t('iam.revoke_gone'));
    return a;
  }

  async function revoke(r: AccessRow) {
    setErr(null);
    const who = principalName(t, r.principal);
    if (r.source === 'node_assignment' && r.role === 'node_admin') return setPreviewRevoke(r);
    const ok = await ask({
      danger: true,
      title: t('iam.revoke_title', { who, where: fullPath(r.scope.path) }),
      body: t('iam.revoke_body_' + r.source, { defaultValue: t('iam.revoke_body') }),
      action: t('iam.revoke_action') + t('ot.reauth_suffix'),
    });
    if (!ok) return;
    try {
      if (r.source === 'node_assignment') await treeApi.revokeAssignment(org, r.scope.id, await currentAssignment(r));
      else if (r.source === 'home_role')
        await kinioFetch(`${planeBase(org.api)}/v1/homes/${encodeURIComponent(r.scope.id)}/members/${encodeURIComponent(r.principal.id)}`, { method: 'DELETE' });
      else if (r.source === 'site_grant') {
        const g = (await orgApi.grants(org, { user_id: r.principal.id })).find((x) => x.grant_id === r.source_ref);
        if (!g) throw new Error(t('iam.revoke_gone'));
        await orgApi.revokeGrant(org, g);
      } else if (r.source === 'share') {
        const home = await findShareHome(org, r);
        if (!home) throw new Error(t('iam.revoke_gone'));
        await shareApi.revoke(org.api, home, r.source_ref);
      }
      setNotice(t('iam.revoked', { who }));
      refresh();
    } catch (e) {
      setErr(e instanceof Error && !('code' in e) ? e.message : otErr(t, e));
    }
  }

  /** 場域授權（整戶）依目前的設備重新展開（IAM-05：之後新增的設備不會自動加入，要刷新）。 */
  async function refreshGrant(r: AccessRow) {
    setErr(null);
    try {
      const g = (await orgApi.grants(org, { user_id: r.principal.id })).find((x) => x.grant_id === r.source_ref);
      if (!g) throw new Error(t('iam.revoke_gone'));
      await orgApi.refreshGrant(org, g);
      setNotice(t('iam.refreshed', { who: principalName(t, r.principal) }));
      refresh();
    } catch (e) {
      setErr(e instanceof Error && !('code' in e) ? e.message : otErr(t, e));
    }
  }

  const groupOpts = groups.data ?? [];
  return (
    <section className={compact ? 'access-list' : 'card access-list'} data-testid="access-list">
      {(filters || !compact) && (
        <div className="toolbar wrap">
          {filters && (
            <>
              <select className="input" style={{ width: 'auto' }} aria-label={t('iam.filter_who')} value={f.principal} onChange={(e) => setF('principal', e.target.value)}>
                <option value="">{t('iam.filter_who_all')}</option>
                {(members.data ?? []).map((m) => (
                  <option key={m.user_id} value={m.user_id}>
                    {m.display_name || m.email || t('iam.left_member')}
                  </option>
                ))}
                {groupOpts.map((g) => (
                  <option key={g.group_id} value={g.group_id}>
                    ▦ {g.name}
                  </option>
                ))}
              </select>
              <select className="input" style={{ width: 'auto' }} aria-label={t('iam.filter_where')} value={f.node} onChange={(e) => setF('node', e.target.value)}>
                <option value="">{t('iam.filter_where_all')}</option>
                {nodeOpts.map((r) => (
                  <option key={r.site.site_id} value={r.site.site_id}>
                    {r.path}
                  </option>
                ))}
              </select>
              <select className="input" style={{ width: 'auto' }} aria-label={t('iam.filter_source')} value={f.source} onChange={(e) => setF('source', e.target.value)}>
                <option value="">{t('iam.filter_source_all')}</option>
                {ACCESS_SOURCES.map((s) => (
                  <option key={s} value={s}>
                    {t('iam.src_' + s)}
                  </option>
                ))}
              </select>
            </>
          )}
          <div className="spacer" />
          <button className="btn btn-primary btn-sm" onClick={() => setGranting(true)} data-testid="grant-open">
            {t(node ? 'iam.assign_site_role' : 'iam.grant_btn')}
          </button>
        </div>
      )}
      {compact && (
        <div className="row-end">
          <button className="btn btn-primary btn-sm" onClick={() => setGranting(true)} data-testid="grant-open">
            {t(node ? 'iam.assign_site_role' : 'iam.grant_btn')}
          </button>
        </div>
      )}
      {notice && (
        <p className="notice" role="status">
          {notice}
        </p>
      )}
      {err && (
        <p className="error" role="alert">
          {err}
        </p>
      )}
      {q.isLoading && <p className="muted">{t('common.loading')}</p>}
      {q.isError && <p className="error">{otErr(t, q.error)}</p>}
      {q.isSuccess && rows.length === 0 && <p className="muted">{t('iam.empty')}</p>}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="grid ot-cards">
            <thead>
              <tr>
                {!principal && <th>{t('iam.col_who')}</th>}
                <th>{t('iam.col_where')}</th>
                <th>{t('iam.col_what')}</th>
                <th>{t('iam.col_source')}</th>
                <th>{t('iam.col_term')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.source}:${r.source_ref}:${r.principal.id}:${r.scope.id}`} data-access-row={r.source} data-who={principalName(t, r.principal).replace(/^▦ /, '')}>
                  {!principal && (
                    <td data-label={t('iam.col_who')}>
                      <div>{principalName(t, r.principal)}</div>
                      {r.principal.kind === 'user' && r.principal.email && r.principal.display_name && <div className="muted small">{r.principal.email}</div>}
                    </td>
                  )}
                  <td data-label={t('iam.col_where')} className="small" data-testid="access-where">
                    {where(r)}
                    {r.source === 'node_assignment' && r.scope.kind === 'node' && <div className="muted small">{t('iam.subtree_scope')}</div>}
                    {r.source === 'site_grant' && r.scope.kind === 'home' && <div className="muted small">{t('iam.all_now')}</div>}
                  </td>
                  <td data-label={t('iam.col_what')} className="small">
                    {rowWhat(t, r, words)}
                    {r.source === 'node_assignment' && r.role === 'node_admin' && <div className="muted small">{t('iam.site_admin_help')}</div>}
                  </td>
                  <td data-label={t('iam.col_source')} className="small">
                    {r.inherited_from ? (
                      <span>{t('iam.inherited_from', { path: fullPath(r.inherited_from.path) })}</span>
                    ) : (
                      <span className="chip">{t('iam.src_' + r.source)}</span>
                    )}
                  </td>
                  <td data-label={t('iam.col_term')} className="small muted">
                    {r.source === 'org_role' || r.source === 'home_role' ? t('ot.forever') : rowTerm(t, r, i18n.language)}
                  </td>
                  <td>
                    {r.source === 'delegation' && !r.inherited_from ? (
                      <Link className="btn btn-sm" to={`/people/external/${encodeURIComponent(r.source_ref)}`}>
                        {t('iam.details')}
                      </Link>
                    ) : r.source === 'org_role' ? (
                      // 組織角色：〔變更角色…〕開成員抽屜（移出組織只在抽屜的維護區，B2-06）；抽屜裡已經是這個人，不再畫
                      !principal &&
                      r.can_revoke &&
                      r.principal.kind === 'user' && (
                        <Link className="btn btn-sm" to={`/people/access?member=${encodeURIComponent(r.principal.id)}`} data-testid="access-change-role">
                          {t('iam.change_role')}
                        </Link>
                      )
                    ) : (
                      r.can_revoke && (
                        <div className="row">
                          {r.source === 'site_grant' && r.scope.kind === 'home' && (
                            <button className="btn btn-sm" title={t('iam.refresh_hint')} onClick={() => void refreshGrant(r)}>
                              {t('iam.refresh')}
                            </button>
                          )}
                          <button className="btn btn-sm btn-danger-outline" onClick={() => void revoke(r)}>
                            {t('iam.revoke')}
                          </button>
                        </div>
                      )
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {q.hasNextPage && (
        <button className="btn btn-sm" disabled={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
          {t('iam.more')}
        </button>
      )}
      <GrantWizard
        org={org}
        open={granting}
        preset={grantPreset ?? (node ? { node } : principal ? { principal: { kind: 'user', id: principal } } : undefined)}
        close={() => setGranting(false)}
        done={(m) => (setNotice(m), refresh())}
      />
      {previewRevoke && (
        <PreviewDialog
          open
          title={t('ot.revoke_assign_title', { who: principalName(t, previewRevoke.principal), node: previewRevoke.scope.path })}
          intro={t('ot.revoke_node_admin_intro')}
          target={previewRevoke.scope.path}
          load={() => treeApi.previewRevoke(org, previewRevoke.scope.id, previewRevoke.source_ref)}
          submit={async (p) => treeApi.revokeAssignment(org, previewRevoke.scope.id, await currentAssignment(previewRevoke), { preview_token: p.token, also_revoke: p.alsoRevoke })}
          action={t('ot.revoke_assign')}
          onDone={() => (setPreviewRevoke(null), setNotice(t('ot.revoked')), refresh())}
          onClose={() => setPreviewRevoke(null)}
        />
      )}
      {dialog}
    </section>
  );
}
